import React from "react";
import { Link } from "react-router-dom";
import { Car, ArrowLeft, Navigation2, Check } from "lucide-react";
import { ThemeToggle } from "@/components/ThemeToggle";
import { SeoFooter } from "@/components/SeoFooter";
import { useSeo, useJsonLd, ogImageUrl } from "@/lib/seo";
import { MODELS, MODEL_PATH } from "@/lib/models";

const ROWS = [
  { key: "energy", label: "Énergie" },
  { key: "Autonomie", label: "Autonomie" },
  { key: "Recharge", label: "Recharge" },
  { key: "Type de prise", label: "Type de prise" },
  { key: "Vitesse", label: "Vitesse" },
];

function specValue(model, label) {
  if (label === "Énergie") return model.energy;
  const s = (model.specs || []).find((x) => x.label.toLowerCase().startsWith(label.toLowerCase().slice(0, 6)));
  return s ? s.value : "—";
}

export default function CompareModels() {
  const path = "/comparatif-voiture-sans-permis";
  useSeo(
    "Comparatif voitures sans permis : autonomie & recharge | vspo",
    "Comparatif des voitures sans permis (Citroën Ami, Aixam, Ligier, Microcar, Chatenet, Bellier) : autonomie, recharge, type de prise et vitesse côte à côte.",
    path,
    ogImageUrl({ title: "Comparatif des modèles", subtitle: "Voiture sans permis" })
  );

  const origin = typeof window !== "undefined" ? window.location.origin : "";
  useJsonLd("compare-jsonld", {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Accueil", item: `${origin}/` },
      { "@type": "ListItem", position: 2, name: "Comparatif des modèles", item: `${origin}${path}` },
    ],
  });

  return (
    <div className="min-h-screen w-full overflow-y-auto bg-background">
      <header className="sticky top-0 z-10 border-b border-border bg-card/95 backdrop-blur-md">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-4 py-3">
          <div className="flex items-center gap-2">
            <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary text-primary-foreground"><Car className="h-5 w-5" /></span>
            <div className="leading-tight">
              <div className="font-display text-lg font-bold text-foreground">vspo</div>
              <div className="text-[11px] text-muted-foreground">GPS voiture sans permis · 45 km/h</div>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <ThemeToggle />
            <Link to="/" className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-3 py-2 text-sm font-semibold text-primary-foreground" data-testid="compare-open-app">
              <ArrowLeft className="h-4 w-4" /> Ouvrir le GPS
            </Link>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-4 pb-16 pt-6">
        <nav className="mb-3 text-xs text-muted-foreground" aria-label="Fil d'ariane">
          <Link to="/" className="hover:text-primary">Accueil</Link> · <span className="text-foreground">Comparatif des modèles</span>
        </nav>
        <h1 className="font-display text-3xl font-bold text-foreground" data-testid="compare-title">
          Comparatif des voitures sans permis
        </h1>
        <p className="mt-3 max-w-2xl text-[15px] leading-relaxed text-muted-foreground">
          Autonomie, recharge, type de prise et vitesse des principaux modèles de voiture sans permis (VSP),
          côte à côte. Les valeurs varient selon la version : vérifiez toujours la fiche du modèle exact.
        </p>

        <div className="mt-6 overflow-x-auto rounded-2xl border border-border">
          <table className="w-full border-collapse text-sm" data-testid="compare-table">
            <thead>
              <tr className="bg-secondary/50">
                <th className="sticky left-0 z-10 bg-secondary/50 p-3 text-left font-display text-foreground">Critère</th>
                {MODELS.map((m) => (
                  <th key={m.slug} className="min-w-[150px] p-3 text-left font-display text-foreground">
                    <Link to={`${MODEL_PATH}/${m.slug}`} className="hover:text-primary" data-testid={`compare-model-${m.slug}`}>{m.name}</Link>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {ROWS.map((row) => (
                <tr key={row.label} className="border-t border-border">
                  <th className="sticky left-0 z-10 bg-card p-3 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground">{row.label}</th>
                  {MODELS.map((m) => (
                    <td key={m.slug} className="p-3 align-top text-foreground">{specValue(m, row.label)}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <ul className="mt-6 space-y-2 text-sm text-muted-foreground">
          <li className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 text-primary" /> Toutes ces VSP sont bridées à 45 km/h et interdites sur autoroutes et voies rapides.</li>
          <li className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 text-primary" /> vspo calcule des itinéraires adaptés et peut proposer un arrêt recharge pour les modèles électriques.</li>
        </ul>

        <div className="mt-8 rounded-2xl border border-border bg-card p-5">
          <h2 className="font-display text-lg font-bold text-foreground">Planifiez un trajet adapté</h2>
          <p className="mt-1 text-sm text-muted-foreground">Quel que soit votre modèle, vspo évite autoroutes et voies rapides et calcule à 45 km/h.</p>
          <Link to="/" className="mt-3 inline-flex items-center gap-2 rounded-xl bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground" data-testid="compare-cta">
            <Navigation2 className="h-4 w-4" /> Ouvrir le GPS
          </Link>
        </div>
      </main>

      <SeoFooter />
    </div>
  );
}
